/** @jsxImportSource @emotion/react */

import { useMutation } from '@apollo/client'
import Button from '@mui/material/Button'
import MenuItem from '@mui/material/MenuItem'
import Typography from '@mui/material/Typography'
import { FC } from 'react'
import { Form } from 'react-final-form'
import { MenuSelectField } from 'components/Select/MenuSelectField'
import { GroupIconsAWS } from 'constants/consts'

import { createCheatSheetGroup, createCheatSheetGroupVariables } from '__generatedTypes__/createCheatSheetGroup'
import { findAllCheatSheetGroup_findAllCheatSheetGroup_items as IGroup } from '__generatedTypes__/findAllCheatSheetGroup'
import { GroupIcon } from '__generatedTypes__/globalTypes'
import { removeCheatSheetGroup, removeCheatSheetGroupVariables } from '__generatedTypes__/removeCheatSheetGroup'
import { updateCheatSheetGroup, updateCheatSheetGroupVariables } from '__generatedTypes__/updateCheatSheetGroup'
import { ReactComponent as TrashIcon } from 'assets/icons/trash.svg'
import TextareaAutosize from 'components/TextareaAutosize/TextareaAutosize'
import client from 'configs/apolloClient'
import {
  CREATE_CHEAT_SHEET_GROUP,
  FIND_ALL_CHEAT_SHEET_GROUP,
  REMOVE_CHEAT_SHEET_GROUP,
  UPDATE_CHEAT_SHEET_GROUP,
} from 'queries/cheatSheetGroup'
import { InputField } from 'components/Input'
import { validateRequired } from 'utils/validation'
import { FieldValues } from 'utils/types'
import { toast } from 'utils/toast'
import * as commonClasses from 'components/shared/GroupCards/commonStyles'
import * as classes from './styles'

interface IProps {
  group: IGroup | null
  availableGroupColors: string[]
  handleClose: () => void
  newGroupOrder: number
  selectedSubjectId: string
}

const ManageCheatSheetsGroupModal: FC<IProps> = ({
  group,
  newGroupOrder,
  selectedSubjectId,
  availableGroupColors,
  handleClose,
}) => {
  const colors = group ? [...availableGroupColors, group.color] : availableGroupColors
  const [updateCheatSheetGroup, { loading: updateLoading }] = useMutation<
    updateCheatSheetGroup,
    updateCheatSheetGroupVariables
  >(UPDATE_CHEAT_SHEET_GROUP)

  const [removeCheatSheetGroup] = useMutation<removeCheatSheetGroup, removeCheatSheetGroupVariables>(
    REMOVE_CHEAT_SHEET_GROUP
  )

  const handleCloseAndUpdate = () => {
    handleClose()
    client.refetchQueries({
      include: [FIND_ALL_CHEAT_SHEET_GROUP],
    })
  }

  const [createCheatSheetGroup, { loading: createLoading }] = useMutation<
    createCheatSheetGroup,
    createCheatSheetGroupVariables
  >(CREATE_CHEAT_SHEET_GROUP)

  const handleGroupRemove = async () => {
    if (!group?.id) return
    try {
      //TODO Error handling
      await removeCheatSheetGroup({ variables: { removeCheatSheetGroupId: group.id } })
      handleCloseAndUpdate()

      toast.success('Group deleted successfully')
    } catch (error) {
      toast.error('Failed to delete group')
    }
  }

  // TODO: specify proper type
  const handleSubmit = async (data: FieldValues) => {
    if (group) {
      try {
        await updateCheatSheetGroup({
          variables: {
            updateCheatSheetGroupInputFieldName: {
              id: data.id,
              color: data.color,
              title: data.title,
              description: data.description ?? '',
              icon: data.icon,
            },
          },
        })
        handleCloseAndUpdate()

        toast.success('Group updated successfully')
      } catch (error) {
        toast.error('Failed to update group')
      }
    } else {
      try {
        await createCheatSheetGroup({
          variables: {
            createCheatSheetGroupInput: {
              color: data.color,
              title: data.title,
              description: data.description ?? '',
              icon: data.icon,
              order: newGroupOrder,
              subject: {
                id: selectedSubjectId,
              },
            },
          },
        })
        handleCloseAndUpdate()

        toast.success('Group created successfully')
      } catch (error) {
        toast.error('Failed to create group')
      }
    }
  }

  return (
    <div css={commonClasses.modalContainer}>
      <Form
        onSubmit={handleSubmit}
        initialValues={group}
        render={({ handleSubmit, values }) => (
          <form css={commonClasses.modalForm} onSubmit={handleSubmit}>
            <Typography css={commonClasses.modalTitle} variant="title2">
              {group ? 'Edit Group' : 'Create Group'}
            </Typography>
            <InputField name="title" validate={validateRequired} label="Group Title" fullWidth />
            <div css={classes.titleColor}>
              <Typography variant="body2" css={commonClasses.label}>
                Color
              </Typography>
              <Typography variant="body2" css={commonClasses.label}>
                Category
              </Typography>
              <MenuSelectField css={commonClasses.select} fullWidth name="color" required>
                {colors.map((color) => (
                  <MenuItem value={color} key={color} css={classes.menuItem}>
                    <div css={classes.menuItemColor(color)} />
                  </MenuItem>
                ))}
              </MenuSelectField>
              <MenuSelectField css={commonClasses.select} fullWidth name="icon" required>
                {Object.values(GroupIcon).map((icon: GroupIcon) => (
                  <MenuItem value={icon} key={icon} css={classes.menuItem}>
                    <img src={GroupIconsAWS[icon]} alt="Group Icons" css={classes.icon} />
                  </MenuItem>
                ))}
              </MenuSelectField>
            </div>
            <TextareaAutosize
              value={values.description}
              css={commonClasses.descriptionInput}
              name="description"
              label="Description"
            />
            <div css={commonClasses.buttons}>
              {!!group && (
                <Button
                  css={commonClasses.button}
                  variant="outlined"
                  color="red"
                  size="large"
                  onClick={handleGroupRemove}
                >
                  <TrashIcon />
                  Delete
                </Button>
              )}
              <Button css={commonClasses.button} variant="outlined" color="blue" size="large" onClick={handleClose}>
                Cancel
              </Button>
              <Button
                css={[commonClasses.button, !!group && commonClasses.fullButton]}
                variant="contained"
                type="submit"
                color="blue"
                size="large"
              >
                {/*TODO Change loading after Nazrin`s answer*/}
                {createLoading || updateLoading ? '...Loading' : 'Submit'}
              </Button>
            </div>
          </form>
        )}
      />
    </div>
  )
}

export default ManageCheatSheetsGroupModal
